/**
 * FeatureRequestPage - Feature request voting system
 * 
 * Features:
 * - Users can request new features
 * - Vote on existing feature requests
 * - Most voted features rise to top
 * - Status tracking (requested, planned, in progress, done)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc,
  doc, 
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../utils/firebase-config';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SignInPrompt from '../Components/SignInPrompt';
import './labcss/community-features.css';

const STATUS_OPTIONS = [
  { id: 'requested', label: 'Requested', color: '#9e9e9e', icon: '📝' },
  { id: 'planned', label: 'Planned', color: '#2196f3', icon: '📋' },
  { id: 'in_progress', label: 'In Progress', color: '#ff9800', icon: '🔨' },
  { id: 'done', label: 'Completed', color: '#4caf50', icon: '✅' },
];

const CATEGORY_OPTIONS = [
  { id: 'calculator', label: 'Calculator', icon: '🧮' },
  { id: 'recipes', label: 'Recipes', icon: '📖' },
  { id: 'inventory', label: 'Inventory', icon: '📦' },
  { id: 'batches', label: 'Batches', icon: '📋' },
  { id: 'labels', label: 'Labels', icon: '🏷️' },
  { id: 'business', label: 'Business', icon: '💼' },
  { id: 'community', label: 'Community', icon: '🌐' },
  { id: 'other', label: 'Other', icon: '💡' },
];

// Feature Request Card
const FeatureCard = ({ feature, currentUser, onVote }) => {
  const hasVoted = feature.votedBy?.includes(currentUser?.uid);
  const status = STATUS_OPTIONS.find(s => s.id === feature.status) || STATUS_OPTIONS[0];
  const category = CATEGORY_OPTIONS.find(c => c.id === feature.category) || CATEGORY_OPTIONS[7];

  return (
    <div className={`ftr-card ftr-status-${feature.status}`}>
      <div className="ftr-vote">
        <button 
          className={`ftr-vote-btn ${hasVoted ? 'ftr-voted' : ''}`}
          onClick={() => onVote(feature.id, hasVoted)}
          title={hasVoted ? 'Remove vote' : 'Vote for this feature'}
        >
          <span className="ftr-vote-icon">▲</span>
          <span className="ftr-vote-count">{feature.votes || 0}</span>
        </button>
      </div>

      <div className="ftr-body">
        <div className="ftr-card-head">
          <h3 className="ftr-title">{feature.title}</h3>
          <div className="ftr-badges">
            <span className="ftr-badge ftr-badge-cat" title="Category">
              {category.label}
            </span>
            <span 
              className="ftr-badge ftr-badge-status" 
              style={{ background: status.color }}
              title="Status"
            >
              {status.label}
            </span>
          </div>
        </div>

        <p className="ftr-desc">{feature.description}</p>

        <div className="ftr-footer">
          <span>Requested by {feature.authorName || 'Anonymous'}</span>
          <span>{feature.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}</span>
        </div>
      </div>
    </div>
  );
};

// Create Feature Request Modal
const CreateRequestModal = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (title.trim() && description.trim()) {
      onSubmit({ title, description, category });
      setTitle('');
      setDescription('');
      setCategory('other');
      onClose();
    }
  };

  return (
    <div className="ftr-modal-overlay" onClick={onClose}>
      <div className="ftr-modal" onClick={e => e.stopPropagation()}>
        <div className="ftr-modal-head">
          <h2>Request a Feature</h2>
          <button className="ftr-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="ftr-modal-body">
          <div className="ftr-field">
            <label>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORY_OPTIONS.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="ftr-field">
            <label>Feature Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief title for your feature request"
              maxLength={100}
            />
          </div>

          <div className="ftr-field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the feature you'd like to see. What problem does it solve? How should it work?"
              rows={5}
            />
          </div>
        </div>

        <div className="ftr-modal-foot">
          <button className="ftr-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="ftr-modal-submit" onClick={handleSubmit}>Submit Request</button>
        </div>
      </div>
    </div>
  );
};

// Main Feature Request Page
export default function FeatureRequestPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('votes');
  const [message, setMessage] = useState(null);

  // Load feature requests
  useEffect(() => {
    const q = query(
      collection(db, 'featureRequests'),
      orderBy('votes', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const featuresData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeatures(featuresData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const [signInPrompt, setSignInPrompt] = useState('');
  const promptLogin = (intent) => setSignInPrompt(intent);

  const goToLogin = () => {
    navigate('/login', { state: { from: { pathname: '/feature-requests' } } });
  };

  // Create feature request
  const handleCreateRequest = async (requestData) => {
    if (!user) {
      promptLogin('submit a request');
      return;
    }

    try {
      await addDoc(collection(db, 'featureRequests'), {
        ...requestData,
        authorId: user.uid,
        authorName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        votes: 1,
        votedBy: [user.uid],
        status: 'requested',
        createdAt: serverTimestamp(),
      });
      setMessage({ type: 'success', text: 'Feature request submitted!' });
    } catch (error) {
      console.error('Error creating request:', error);
      setMessage({ type: 'error', text: 'Failed to submit request' });
    }
  };

  // Vote for feature
  const handleVote = async (featureId, hasVoted) => {
    if (!user) {
      promptLogin('vote');
      return;
    }

    const featureRef = doc(db, 'featureRequests', featureId);

    try {
      if (hasVoted) {
        await updateDoc(featureRef, {
          votes: increment(-1),
          votedBy: arrayRemove(user.uid)
        });
      } else {
        await updateDoc(featureRef, {
          votes: increment(1),
          votedBy: arrayUnion(user.uid)
        });
      }
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  // Filter and sort features
  const filteredFeatures = features
    .filter(feature => {
      const matchesStatus = filterStatus === 'all' || feature.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || feature.category === filterCategory;
      return matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'votes') return (b.votes || 0) - (a.votes || 0);
      if (sortBy === 'newest') {
        return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
      }
      return 0;
    });

  // Stats
  const totalRequests = features.length;
  const completedRequests = features.filter(f => f.status === 'done').length;
  const inProgressRequests = features.filter(f => f.status === 'in_progress').length;

  return (
    <>
      <div className="sl-page feature-request-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Feature Requests' }
          ]} />
          <div className="pageheader-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div className="pageheading">Feature Requests</div>
              <div className="pagedescription">Help shape the future of Soap Lab! Vote on features you want or suggest new ones.</div>
            </div>
            <button 
              className="ftr-header-btn"
              onClick={() => user ? setShowCreateModal(true) : promptLogin('submit a request')}
            >
              Request Feature
            </button>
          </div>
        </div>

        <div className="feature-request-page-content borderleft">
          <section className="mypadding borderbottom">
            {message && (
              <div className={`ftr-message ${message.type}`}>
                {message.text}
                {message.action === 'login' && (
                  <button className="ftr-message-action" onClick={goToLogin}>Log in</button>
                )}
                <button className="ftr-message-close" onClick={() => setMessage(null)}>×</button>
              </div>
            )}

            {/* Stats */}
            <div className="ftr-stats">
              <div className="ftr-stat">
                <span className="ftr-stat-value">{totalRequests}</span>
                <span className="ftr-stat-label">Total Requests</span>
              </div>
              <div className="ftr-stat">
                <span className="ftr-stat-value">{inProgressRequests}</span>
                <span className="ftr-stat-label">In Progress</span>
              </div>
              <div className="ftr-stat">
                <span className="ftr-stat-value">{completedRequests}</span>
                <span className="ftr-stat-label">Completed</span>
              </div>
            </div>

            {/* Filters */}
            <div className="ftr-filters">
              <div className="ftr-filter-group">
                <label>Status:</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="all">All</option>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="ftr-filter-group">
                <label>Category:</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="all">All</option>
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="ftr-filter-group">
                <label>Sort:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="votes">Most Voted</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>

            {/* Features List */}
            {loading ? (
              <div className="ftr-loading">Loading feature requests...</div>
            ) : filteredFeatures.length === 0 ? (
              <div className="ftr-empty">
                <h3>No feature requests yet</h3>
                <p>Be the first to suggest a new feature!</p>
                <button className="ftr-empty-btn" onClick={() => user ? setShowCreateModal(true) : promptLogin('submit a request')}>
                  Submit Request
                </button>
              </div>
            ) : (
              <div className="ftr-list">
                {filteredFeatures.map((feature, index) => (
                  <div key={feature.id} className="ftr-rank-wrapper">
                    <span className="ftr-rank">#{index + 1}</span>
                    <FeatureCard
                      feature={feature}
                      currentUser={user}
                      onVote={handleVote}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <CreateRequestModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRequest}
        />
      <SignInPrompt intent={signInPrompt} onClose={() => setSignInPrompt('')} />
      <Footer />
      </div>
    </>
  );
}
